/*
  Lichcards 13-10-2026 — "Vaak samen gekocht" in de winkelwagenlade (markup: snippets/lc-fbt.liquid).
  Eén klik op "Toevoegen" legt het product in de winkelwagen. Staat de lade open (normaal geval), dan wordt alleen de
  inhoud ververst, net als bij het wijzigen van een aantal (cart-drawer-items.updateSections), zodat de lade niet
  opnieuw inschuift. Anders (lade intussen gesloten) via cart-drawer.renderContents, zoals de productpagina.
  Daarna staat het product in de lijst erboven en verdwijnt het uit de suggesties. Schermlezers horen
  "… is toegevoegd aan je winkelwagen"; de focus gaat naar de volgende suggestie (of de afrekenknop).
*/
(() => {
  if (window.lcFbtReady) return;
  window.lcFbtReady = true;

  const cartError = () => (window.cartStrings && window.cartStrings.error) || 'Error';
  let cartNavigationPending = false;

  const requestCartJSON = (url, options, variantId) => {
    const controller = new AbortController();
    let timeout;
    const request = Promise.resolve().then(() => fetch(url, { ...options, signal: controller.signal }))
      .then(async response => {
        const data = await response.json();
        const status = response.ok ? Number(data?.status) : response.status;
        const knownRejection = status >= 400 && status < 500 &&
          [data?.description, data?.message, data?.errors].some(value => typeof value === 'string' && value.trim());
        const items = Array.isArray(data?.items) ? data.items : [data];
        const confirmedAdd = items.some(item => String(item?.variant_id || item?.id) === String(variantId) &&
          Number.isSafeInteger(item?.quantity) && item.quantity > 0);
        if (!knownRejection && (!response.ok || data?.status || data?.errors || !confirmedAdd)) {
          throw new Error('Cart add outcome could not be confirmed');
        }
        return { data, knownRejection };
      });
    const deadline = new Promise((resolve, reject) => {
      timeout = window.setTimeout(() => {
        controller.abort();
        reject(new Error('Cart request timed out'));
      }, 12000);
    });
    return Promise.race([request, deadline]).finally(() => window.clearTimeout(timeout));
  };

  const reconcileRejectedCart = async drawer => {
    const started = Date.now();
    let timeout;
    try {
      let ProductForm = customElements.get('product-form-component');
      if (!ProductForm && typeof window.theme?.loadProductFormLibrary === 'function') {
        ProductForm = await Promise.race([
          window.theme.loadProductFormLibrary(),
          new Promise(resolve => { timeout = window.setTimeout(() => resolve(null), 12000); }),
        ]);
      }
      const remaining = 12000 - (Date.now() - started);
      return remaining > 0 && typeof ProductForm?.reconcileRejectedCart === 'function'
        ? await ProductForm.reconcileRejectedCart(drawer, remaining) : false;
    } catch (error) {
      return false;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const recoverUncertainCart = () => {
    if (cartNavigationPending) return;
    cartNavigationPending = true;
    const buttons = Array.from(document.querySelectorAll('[data-lc-fbt-add]'));
    const previous = buttons.map(button => button.getAttribute('aria-disabled'));
    buttons.forEach(button => button.setAttribute('aria-disabled', 'true'));
    const restore = event => {
      if (!event.persisted) return;
      window.removeEventListener('pageshow', restore);
      cartNavigationPending = false;
      buttons.forEach((button, index) => {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
        if (button.disabled || previous[index] === 'true') return;
        if (previous[index] === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', previous[index]);
      });
    };
    window.addEventListener('pageshow', restore);
    window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
  };

  const afterUpdate = (drawer, addedText) => {
    const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
    if (liveRegion && addedText) liveRegion.textContent = addedText;
    const next = drawer.querySelector('[data-lc-fbt-add], .lc-fbt__add') || drawer.querySelector('#CartDrawer-Checkout');
    if (!next || typeof next.focus !== 'function') return;
    next.focus({ preventScroll: true });
    if (typeof drawer.refreshFocusTrap === 'function') drawer.refreshFocusTrap(next);
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-lc-fbt-add]');
    if (!button) return;
    event.preventDefault();
    if (cartNavigationPending || button.disabled || button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true') return;

    const block = button.closest('[data-lc-fbt]');
    const drawer = button.closest('cart-drawer') || document.querySelector('cart-drawer');
    const variantId = button.dataset.lcFbtAdd;
    const title = button.dataset.lcFbtTitle || '';
    const addedTemplate = block?.dataset.lcFbtAdded || '';
    const addedText = addedTemplate ? addedTemplate.replace('[title]', title) : '';
    const error = block?.querySelector('[data-lc-fbt-error]');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }

    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    const sections = drawer && typeof drawer.getSectionsToRender === 'function'
      ? drawer.getSectionsToRender().map((section) => section.id)
      : [];
    if (sections.length) {
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);
    }

    let knownRejection = false;
    try {
      const result = await requestCartJSON((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      }, variantId);
      if (cartNavigationPending) return;
      const data = result.data;
      knownRejection = result.knownRejection;
      if (knownRejection) {
        throw new Error(data.description || data.message || data.errors || cartError());
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lc-fbt', productVariantId: variantId, cartData: data });
      }

      const items = drawer && drawer.querySelector('cart-drawer-items');
      const drawerIsOpen = !!drawer && drawer.classList.contains('open') && !drawer.lcClosing;
      if (drawerIsOpen && data.sections && items && typeof items.updateSections === 'function') {
        items.updateSections(data.sections, data);
        if (typeof drawer.updateCartIconBubble === 'function') await drawer.updateCartIconBubble(data);
        afterUpdate(drawer, addedText);
      } else if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
        await drawer.renderContents(data);
        afterUpdate(drawer, addedText);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        return;
      }
      if (button.isConnected) {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    } catch (e) {
      if (cartNavigationPending) return;
      if (!knownRejection) {
        recoverUncertainCart();
        return;
      }
      const errorMessage = (e && e.message) || cartError();
      if (error) {
        error.textContent = errorMessage;
        error.hidden = false;
      }
      if (!(await reconcileRejectedCart(drawer))) {
        recoverUncertainCart();
        return;
      }
      const isEmpty = drawer?.classList.contains('is-empty');
      const target = drawer?.querySelector(isEmpty ? '[data-cart-drawer-empty]' : '#CartDrawer-Form');
      const currentError = target?.querySelector('[data-lc-fbt-error]') || error;
      if (currentError) {
        if (target && (isEmpty || !currentError.isConnected)) target.append(currentError);
        currentError.textContent = errorMessage;
        currentError.hidden = false;
      }
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  });
})();
